/**
 * App de Mac (ADR-G25). A web fala com ela por endereços nolens-guides://, que o macOS
 * entrega à app: ligar a conta, gravar, guiar e executar um guia. Nenhum destes faz nada
 * sozinho na app (gravar só mostra o painel; executar confirma sempre), porque qualquer
 * página os pode abrir.
 *
 * A ligação é pelo PKCE: a app abre /mac/ligar?state=…&challenge=…; a web pede o código à
 * API e devolve-o à app (nolens-guides://ligar?code=…&state=…). O verifier nunca passa pela web.
 */
import { MAC_URL_SCHEME, type MacLinkCode, type MeResponse } from '@nolens/domain';
import { api } from './api';

export type MacCommand = 'ligar' | 'gravar' | 'guiar' | 'executar';

/** nolens-guides://<comando>?<parâmetros> */
export function macUrl(command: MacCommand, params: Record<string, string> = {}): string {
  const q = new URLSearchParams(params).toString();
  return `${MAC_URL_SCHEME}://${command}${q ? '?' + q : ''}`;
}

/** Abre a app de Mac (o macOS pergunta da primeira vez se pode abrir a app). */
export function openMac(command: MacCommand, params?: Record<string, string>): void {
  window.location.href = macUrl(command, params);
}

/** A app de Mac está ligada a esta conta. */
export const macLinked = (me: Pick<MeResponse, 'macApp'>) => !!me.macApp;

/**
 * Mostrar a app de Mac na web (barra lateral): por agora só ao proprietário da plataforma, ou a
 * quem já a ligou. Os clientes continuam a gravar com a extensão e não a veem (ADR-G25).
 */
export const macVisible = (me: Pick<MeResponse, 'macApp' | 'isPlatformAdmin'>) =>
  me.isPlatformAdmin || macLinked(me);

const STATE_RE = /^[A-Za-z0-9_-]{16,128}$/;
const CHALLENGE_RE = /^[A-Za-z0-9_-]{43}$/;

/** Os parâmetros de /mac/ligar, gerados pela app; null se faltarem ou não forem válidos. */
export function linkParams(search: URLSearchParams): { state: string; challenge: string } | null {
  const state = search.get('state') ?? '';
  const challenge = search.get('challenge') ?? '';
  return STATE_RE.test(state) && CHALLENGE_RE.test(challenge) ? { state, challenge } : null;
}

/** Pede o código de uso único (com a sessão da web) e devolve o endereço que o leva à app. */
export async function macLinkUrl(params: { state: string; challenge: string }): Promise<string> {
  const r = await api<MacLinkCode>('/v1/mac/link', {
    method: 'POST',
    body: { challenge: params.challenge },
  });
  return macUrl('ligar', { code: r.code, state: params.state });
}
